import { Check } from "lucide-react-native";
import { FC } from "react";

import {
  Badge,
  BadgeText,
  Box,
  HStack,
  Image,
  Pressable,
  Skeleton,
  Text,
  VStack,
} from "@/components/ui";
import { useLocalization } from "@/hooks";
import { CustomRange, Panel } from "@/types/resources";

/* ── Helpers ──────────────────────────────────────── */

const MAX_FLAGS = 4;

const flagUrl = (iso2: string): string =>
  `https://flagcdn.com/w40/${iso2.toLowerCase()}.png`;

const findDemographic = (panel: Panel, key: string) =>
  panel.demographics.find((d) => d.demographic === key);

const getCountryCodes = (panel: Panel) => {
  const cs = findDemographic(panel, "country");
  return cs ? cs.distribution.map((d) => String(d.value)) : [];
};

const getAgeRange = (panel: Panel) => {
  const age = findDemographic(panel, "age");
  if (!age || age.distribution.length === 0) return null;
  const values = age.distribution.map((d) => d.value as CustomRange);
  return `${Math.min(...values.map((v) => v.min))}-${Math.max(...values.map((v) => v.max))}`;
};

const TOTAL_GENDER_OPTIONS = 3;

const getGenderBadge = (panel: Panel, allLabel: string): string | null => {
  const g = findDemographic(panel, "gender");
  if (!g || g.distribution.length === 0) return null;
  if (g.distribution.length >= TOTAL_GENDER_OPTIONS) return allLabel;
  if (g.distribution.length === 1) return g.distribution[0].label;
  return g.distribution.map((d) => d.label).join(", ");
};

const getExtraFilterCount = (panel: Panel) =>
  panel.demographics.filter(
    (d) =>
      d.demographic !== "age" &&
      d.demographic !== "country" &&
      d.demographic !== "gender" &&
      d.distribution.length > 0
  ).length;

/* ── Types ────────────────────────────────────────── */

interface PanelCardProps {
  applied: boolean;
  disabled?: boolean;
  onPress?: () => void;
  panel: Panel;
}

/* ── Badges ───────────────────────────────────────── */

const FilterBadge: FC<{ label: string }> = ({ label }) => (
  <Box className="px-1.5 py-0.5 rounded bg-background-100">
    <Text className="text-[10px] text-typography-400">{label}</Text>
  </Box>
);

const DefaultBadge: FC = () => {
  const { i18n } = useLocalization();
  return (
    <Badge
      action="warning"
      className="px-2 py-0.5 rounded-full"
      variant="solid"
    >
      <BadgeText className="normal-case text-2xs font-medium">
        {i18n.t("panels.default").toLowerCase()}
      </BadgeText>
    </Badge>
  );
};

const ArchivedBadge: FC = () => {
  const { i18n } = useLocalization();
  return (
    <Badge action="muted" className="px-2 py-0.5 rounded-full" variant="solid">
      <BadgeText className="normal-case text-2xs font-medium">
        {i18n.t("misc.archived").toLowerCase()}
      </BadgeText>
    </Badge>
  );
};

/* ── Component ────────────────────────────────────── */

const PanelCard: FC<PanelCardProps> = ({
  applied,
  disabled = false,
  onPress,
  panel,
}) => {
  const { i18n } = useLocalization();

  const countryCodes = getCountryCodes(panel);
  const ageRange = getAgeRange(panel);
  const genderBadge = getGenderBadge(
    panel,
    i18n.t("audienceBuilder.allGenders")
  );
  const extraCount = getExtraFilterCount(panel);
  const visibleFlags = countryCodes.slice(0, MAX_FLAGS);
  const hiddenFlagCount = countryCodes.length - visibleFlags.length;

  const content = (
    <Box
      className={`w-full rounded-xl border p-4 bg-white transition-all duration-300 ${
        applied
          ? "border-primary-500"
          : disabled
            ? "border-outline-200"
            : "border-outline-200 hover:border-outline-300 hover:shadow-soft-2 hover:-translate-y-0.5"
      }`}
    >
      <HStack className="items-center gap-1.5 mb-1.5">
        <Text
          className={`text-sm font-semibold ${
            applied ? "text-primary-600" : "text-typography-900"
          }`}
          style={{
            overflow: "hidden",
            // @ts-expect-error - web-only truncation styles
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {panel.name}
        </Text>
        {panel.is_default && <DefaultBadge />}
        {!!panel.archived_at && <ArchivedBadge />}
      </HStack>

      <HStack className="items-center gap-1.5">
        {visibleFlags.length > 0 && (
          <HStack className="items-center gap-0.5">
            {visibleFlags.map((code) => (
              <Image
                alt={code}
                className="h-[14px] w-[20px] rounded-[3px] border border-outline-100"
                key={code}
                size="none"
                source={{ uri: flagUrl(code) }}
              />
            ))}
            {hiddenFlagCount > 0 && (
              <Text className="text-[10px] text-typography-400 ml-0.5">
                +{hiddenFlagCount}
              </Text>
            )}
          </HStack>
        )}
        <Text className="text-[11px] text-typography-400 ml-1">
          {i18n.t("audienceBuilder.countryCount", {
            count: countryCodes.length,
          })}
        </Text>
      </HStack>

      <HStack className="flex-wrap gap-1 mt-3">
        {genderBadge && <FilterBadge label={genderBadge} />}
        {ageRange && <FilterBadge label={ageRange} />}
        {extraCount > 0 && (
          <FilterBadge
            label={i18n.t("audienceBuilder.extraFilters", {
              count: extraCount,
            })}
          />
        )}
      </HStack>

      {applied && (
        <Box className="absolute -top-2 -right-2 w-5 h-5 rounded-full bg-primary-500 items-center justify-center">
          <Check color="rgb(var(--color-typography-0))" size={12} />
        </Box>
      )}
    </Box>
  );

  return disabled ? (
    <Box className="w-full cursor-default web:pointer-events-none">
      {content}
    </Box>
  ) : (
    <Pressable className="w-full" onPress={onPress}>
      {content}
    </Pressable>
  );
};

/* ── Skeleton ─────────────────────────────────────── */

export const PanelCardSkeleton: FC = () => (
  <VStack className="w-full rounded-xl border border-outline-200 p-4">
    <Skeleton className="h-4 w-28 rounded mb-1.5" />
    <HStack className="items-center gap-1.5">
      <HStack className="gap-0.5">
        <Skeleton className="h-[14px] w-[20px] rounded-[3px]" />
        <Skeleton className="h-[14px] w-[20px] rounded-[3px]" />
      </HStack>
      <Skeleton className="h-3 w-16 rounded ml-1" />
    </HStack>
    <HStack className="gap-1 mt-3">
      <Skeleton className="h-5 w-14 rounded" />
      <Skeleton className="h-5 w-14 rounded" />
    </HStack>
  </VStack>
);

export default PanelCard;
